import SearchIcon from "@mui/icons-material/Search";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/** The documentation search input. It is **URL-driven**: the query lives in
 * `/documentation/search?q=…`, so this same component can mount in several
 * places (the sidebar, the results page, the mobile drawer) and they all stay
 * in sync with no shared state — the URL is the single source of truth.
 *
 * - Off the search page it shows empty and the first keystroke navigates TO
 *   the search page (a single history entry, so Back returns where you were).
 * - On the search page each keystroke replaces `?q=` (no history spam) and the
 *   results re-filter live. Clearing the box drops the param. */
export function DocSearchBox({ autoFocus = false }: { autoFocus?: boolean }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const onSearchPage = location.pathname.endsWith("/documentation/search");
  const value = onSearchPage ? params.get("q") ?? "" : "";

  const setValue = (next: string): void => {
    const qs = next.trim() ? `?q=${encodeURIComponent(next)}` : "";
    navigate(`/documentation/search${qs}`, { replace: onSearchPage });
  };

  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      onChange={(e) => setValue(e.target.value)}
      autoFocus={autoFocus}
      placeholder="Search documents…"
      inputProps={{ "aria-label": "Search documents", "data-testid": "doc-search-input" }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => setValue("")}
              sx={{ fontSize: 14, color: "text.disabled" }}
            >
              ✕
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}
